import type { MessageData, PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { faceEnrollmentEndpoints, replaceParams } from "@/app/api/endpoints";
import type {
	CreateFaceEnrollmentRequest,
	FaceEnrollment,
	FaceSyncBulkResponseData,
	FaceSyncResponseData,
	ListFaceEnrollmentParams,
	UpdateFaceEnrollmentRequest,
} from "../types";

type RawFaceEnrollment = Omit<FaceEnrollment, "faceImageUrl" | "ownerName"> & {
	faceImageUrl?: string | null;
	staffName?: string | null;
	ownerName?: string | null;
	file?: {
		url?: string | null;
	} | null;
	faceImage?: {
		url?: string | null;
	} | null;
	staff?: {
		id: string;
		fullname: string;
		phoneNumber?: string | null;
	} | null;
};

function normalizeFaceEnrollment(item: RawFaceEnrollment): FaceEnrollment {
	const { file, faceImage, faceImageUrl, staff, staffName, ownerName, ...rest } = item;
	const resolvedName = ownerName ?? staffName ?? staff?.fullname ?? null;
	const resolvedUrl = faceImageUrl ?? faceImage?.url ?? file?.url ?? null;
	return {
		...rest,
		ownerType: rest.ownerType ?? "staff",
		ownerName: resolvedName,
		staffName: resolvedName,
		faceImageUrl: resolvedUrl,
		staff: staff ?? null,
		faceImage: faceImage ?? (file ? { id: item.faceImageId, url: file.url ?? "" } : null),
	};
}

export const faceEnrollmentService = {
	async list(params: ListFaceEnrollmentParams): Promise<PaginatedResponse<FaceEnrollment>> {
		const response = await apiClient.get<PaginatedResponse<RawFaceEnrollment>>(
			faceEnrollmentEndpoints.list,
			{
				params: {
					page: params.page,
					limit: params.limit,
					organizationId: params.organizationId,
					ownerType: params.ownerType,
				},
			}
		);
		return {
			...response.data,
			data: {
				...response.data.data,
				items: response.data.data.items.map(normalizeFaceEnrollment),
			},
		};
	},

	async getById(id: string): Promise<FaceEnrollment> {
		const url = replaceParams(faceEnrollmentEndpoints.getById, { id });
		const response = await apiClient.get<SuccessResponse<RawFaceEnrollment>>(url);
		return normalizeFaceEnrollment(response.data.data);
	},

	async create(data: CreateFaceEnrollmentRequest): Promise<FaceEnrollment> {
		const response = await apiClient.post<SuccessResponse<RawFaceEnrollment>>(
			faceEnrollmentEndpoints.create,
			data
		);
		return normalizeFaceEnrollment(response.data.data);
	},

	async update(id: string, data: UpdateFaceEnrollmentRequest): Promise<FaceEnrollment> {
		const url = replaceParams(faceEnrollmentEndpoints.update, { id });
		const response = await apiClient.put<SuccessResponse<RawFaceEnrollment>>(url, data);
		return normalizeFaceEnrollment(response.data.data);
	},

	async delete(id: string): Promise<void> {
		const url = replaceParams(faceEnrollmentEndpoints.delete, { id });
		await apiClient.delete<SuccessResponse<MessageData>>(url);
	},

	async sync(faceEnrollmentId: string): Promise<FaceSyncResponseData> {
		const response = await apiClient.post<SuccessResponse<FaceSyncResponseData>>(
			faceEnrollmentEndpoints.sync,
			{ faceEnrollmentId }
		);
		return response.data.data;
	},

	async syncBulk(faceEnrollmentIds: string[]): Promise<FaceSyncBulkResponseData> {
		const response = await apiClient.post<SuccessResponse<FaceSyncBulkResponseData>>(
			faceEnrollmentEndpoints.syncBulk,
			{ faceEnrollmentIds }
		);
		return response.data.data;
	},
};
